const normalizeParticipantIds = (participantIds = []) => [
    ...new Set(participantIds.filter(Boolean).map(String)),
].sort();

const cloneConversation = (conversation) => {
    if (!conversation) return null;
    if (conversation.type === 'group') {
        return { type: 'group', groupId: String(conversation.groupId) };
    }
    return {
        type: 'pending',
        participantIds: normalizeParticipantIds(conversation.participantIds),
    };
};

export const groupConversation = (groupId) => {
    if (!groupId) throw new Error('A group conversation requires a groupId.');
    return { type: 'group', groupId: String(groupId) };
};

export const pendingConversation = (participantIds) => {
    const normalized = normalizeParticipantIds(participantIds);
    if (!normalized.length) {
        throw new Error('A pending conversation requires at least one participant.');
    }
    return { type: 'pending', participantIds: normalized };
};

export const conversationKey = (conversation) => {
    if (!conversation) return null;
    if (conversation.type === 'group') return `group:${conversation.groupId}`;
    if (conversation.type === 'pending') {
        return `pending:${normalizeParticipantIds(conversation.participantIds).join(',')}`;
    }
    throw new Error(`Unsupported conversation type: ${conversation.type}`);
};

export const emptyComposerDraft = () => ({
    html: '',
    attachments: [],
    attachmentNodes: [],
    reply: null,
    replyNodes: [],
});

export const stripComposerHtml = (html) => String(html ?? '')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/(?:p|div|li|h[1-6])>/gi, ' ')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();

export const draftListPreview = (draft, charLimit = 80) => {
    const text = stripComposerHtml(draft?.html);
    const hasAttachments = (draft?.attachments?.length ?? 0) > 0;
    if (text) {
        const clipped = text.length > charLimit ? `${text.slice(0, charLimit)}…` : text;
        return `Draft: ${clipped}`;
    }
    if (hasAttachments) return 'Draft: Attachment';
    if (draft?.reply) return 'Draft: Reply';
    return null;
};

const cloneDraft = (draft = emptyComposerDraft()) => ({
    html: draft.html ?? '',
    attachments: [...(draft.attachments ?? [])],
    attachmentNodes: [...(draft.attachmentNodes ?? [])],
    reply: draft.reply ?? null,
    replyNodes: [...(draft.replyNodes ?? [])],
});

export class ChatComposerState {
    constructor() {
        this.activeConversation = null;
        this.activeKey = null;
        this.panelOpen = false;
        this.drafts = new Map();
    }

    getActiveConversation() {
        return cloneConversation(this.activeConversation);
    }

    getActiveKey() {
        return this.activeKey;
    }

    getDraft(conversation = this.activeConversation) {
        const key = conversationKey(conversation);
        return key && this.drafts.has(key)
            ? cloneDraft(this.drafts.get(key))
            : emptyComposerDraft();
    }

    saveActive(draft) {
        if (!this.activeKey) return;
        this.drafts.set(this.activeKey, cloneDraft(draft));
    }

    select(conversation, outgoingDraft = null) {
        const nextConversation = cloneConversation(conversation);
        const nextKey = conversationKey(nextConversation);
        if (this.activeKey && this.activeKey !== nextKey && outgoingDraft) {
            this.saveActive(outgoingDraft);
        }
        this.activeConversation = nextConversation;
        this.activeKey = nextKey;
        return this.getDraft(nextConversation);
    }

    close(draft) {
        this.saveActive(draft);
        this.panelOpen = false;
        return this.getActiveConversation();
    }

    open(fallbackConversation = null) {
        if (!this.activeConversation && fallbackConversation) {
            this.select(fallbackConversation);
        }
        this.panelOpen = true;
        return {
            conversation: this.getActiveConversation(),
            draft: this.getDraft(),
        };
    }

    createSendTarget() {
        if (!this.activeConversation || !this.activeKey) return null;
        const conversation = this.getActiveConversation();
        return Object.freeze({
            key: this.activeKey,
            groupId: conversation.type === 'group' ? conversation.groupId : null,
            participantIds: Object.freeze(
                conversation.type === 'pending' ? [...conversation.participantIds] : []
            ),
        });
    }

    resolvePending(sendTarget, groupId) {
        const resolved = groupConversation(groupId);
        const resolvedKey = conversationKey(resolved);
        if (sendTarget?.key && this.drafts.has(sendTarget.key)) {
            this.drafts.set(resolvedKey, this.drafts.get(sendTarget.key));
            this.drafts.delete(sendTarget.key);
        }
        if (sendTarget?.key && this.activeKey === sendTarget.key) {
            this.activeConversation = resolved;
            this.activeKey = resolvedKey;
        }
        return resolved;
    }

    clearDraft(key = this.activeKey) {
        if (key) this.drafts.delete(key);
    }
}
